import { AsyncSelectInput } from '@/components/Forms/AsyncSelect';
import FormSection from '@/components/Forms/FormSection';
import { debounceAsync } from '@/helpers/debounceAsync';
import { formatGeneratorPowerRating } from '@/helpers/formatGeneratorPowerRating';
import { intercomTarget } from '@/helpers/intercomTarget';
import usePermissions from '@/hooks/usePermissions';
import { useSearchProductsMutation } from '@/services/productCatalog';
import { Box, Button, Modal, RadioGroup, TextInput, toast, useOpenClose } from '@palmetto/palmetto-components';
import { ChangeEvent, FocusEvent, useCallback, useEffect, useMemo } from 'react';
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { Link } from 'react-router-dom';

import {
  GeneratorEquipmentItem,
  GeneratorEquipmentType,
  GeneratorEquipmentTypeLabels,
  GeneratorFuelType,
  GeneratorFuelTypeLabels,
  UserPermissions,
} from 'types';

interface EquipmentAndMaterialsSectionProps {
  systemIndex: number;
  isPreliminary?: boolean;
  helpLink?: string;
  isPackageEditable?: boolean;
}

const dataKey = 'equipment' as const;
const label = 'Equipment & Materials' as const;

const fuelTypeOptions = Object.values(GeneratorFuelType).map((type) => ({
  id: type,
  label: GeneratorFuelTypeLabels[type],
  value: type,
}));

interface PreliminaryEquipmentRowProps {
  systemIndex: number;
  index: number;
  isDisabled: boolean;
  onQuantityChange: (index: number, value: number) => void;
}

const PreliminaryEquipmentRow = ({
  systemIndex,
  index,
  isDisabled,
  onQuantityChange,
}: PreliminaryEquipmentRowProps) => {
  const { control, setValue } = useFormContext<any>();
  const fuelTypeName = `systems.${systemIndex}.${dataKey}.items.${index}.fuelType`;
  const quantityName = `systems.${systemIndex}.${dataKey}.items.${index}.quantity`;

  const handleQuantityChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>, fieldOnChange: (v: number | string) => void) => {
      fieldOnChange(event.target.value);
      const value = parseInt(event.target.value, 10);
      if (!isNaN(value) && value > 1) {
        onQuantityChange(index, value);
      }
    },
    [onQuantityChange, index],
  );

  const handleQuantityBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>, fieldOnBlur: () => void) => {
      if (event.target.value?.trim() === '') {
        setValue(quantityName, 1, { shouldDirty: true });
      }
      fieldOnBlur();
    },
    [setValue, quantityName],
  );

  return (
    <Box
      direction={{ base: 'column', tablet: 'row' }}
      gap="lg"
      padding={{ base: 'md', tablet: 'lg' }}
      background="secondary"
      radius="md"
    >
      <Box flex="auto" gap="lg" minWidth="0">
        <Box direction={{ base: 'column', desktop: 'row' }} gap={{ base: 'lg', desktop: 'xl' }}>
          <Box flex="auto" minWidth="0">
            <Box fontSize="sm" fontWeight="medium" margin="0 0 sm 0">
              Fuel Type
            </Box>
            <Controller
              control={control}
              name={fuelTypeName}
              render={({ field }) => (
                <RadioGroup
                  name={fuelTypeName}
                  value={field.value ?? ''}
                  options={fuelTypeOptions}
                  isDisabled={isDisabled}
                  onChange={(e) => field.onChange(e.target.value)}
                  onBlur={field.onBlur}
                />
              )}
            />
          </Box>
        </Box>
      </Box>
      <Box
        direction={{ base: 'row', tablet: 'column' }}
        gap="sm"
        alignItems={{ base: 'flex-end', tablet: 'flex-start' }}
        justifyContent={{ base: 'space-between', tablet: 'flex-start' }}
        flex={{ tablet: 'none' }}
      >
        <Box width={{ base: '2xl', tablet: 'lg' }}>
          <Controller
            control={control}
            name={quantityName}
            render={({ field }) => (
              <TextInput
                id={quantityName}
                name={quantityName}
                label="Quantity"
                type="number"
                value={field.value ?? ''}
                onChange={(e) => handleQuantityChange(e as ChangeEvent<HTMLInputElement>, field.onChange)}
                onBlur={(e) => handleQuantityBlur(e, field.onBlur)}
                isDisabled={isDisabled}
                inputProps={{ min: 1, max: 10 }}
              />
            )}
          />
        </Box>
      </Box>
    </Box>
  );
};

interface FinalEquipmentRowProps {
  systemIndex: number;
  index: number;
  isDisabled: boolean;
  searchProducts: (input: string, productType: GeneratorEquipmentType) => Promise<any>;
}

const FinalEquipmentRow = ({ systemIndex, index, isDisabled, searchProducts }: FinalEquipmentRowProps) => {
  const { control, setValue } = useFormContext<any>();
  const item: (GeneratorEquipmentItem & { isSerialNumberRequired?: boolean; productId?: string }) | undefined =
    useWatch({ control, name: `systems.${systemIndex}.${dataKey}.items.${index}` });

  const setEquipmentField = useCallback(
    (key: string, value: any) => {
      setValue(`systems.${systemIndex}.${dataKey}.items.${index}.${key}`, value, { shouldDirty: true });
    },
    [setValue, systemIndex, index],
  );

  const handleChangeProduct = useCallback(() => {
    setEquipmentField('manufacturer', '');
    setEquipmentField('model', '');
    setEquipmentField('productId', undefined);
    setEquipmentField('name', '');
  }, [setEquipmentField]);

  const handleSelectProduct = useCallback(
    (event: any) => {
      const v = event?.target?.value;
      if (v && v.value) {
        setEquipmentField('manufacturer', v.manufacturer);
        setEquipmentField('model', v.model);
        setEquipmentField('lpPowerRating', v.lpPowerRating);
        setEquipmentField('gasPowerRating', v.gasPowerRating);
        setEquipmentField('quantity', 1);
        setEquipmentField('productId', v.value);
        setEquipmentField('name', v.name);
      }
    },
    [setEquipmentField],
  );

  const fuelTypeName = `systems.${systemIndex}.${dataKey}.items.${index}.fuelType`;

  if (!item) return null;

  const showSearchSelector = !(item.manufacturer && item.manufacturer !== '') && !(item.model && item.model !== '');

  const powerRating = formatGeneratorPowerRating(item.lpPowerRating, item.gasPowerRating);

  return (
    <Box
      direction={{ base: 'column', tablet: 'row' }}
      gap="lg"
      padding={{ base: 'md', tablet: 'lg' }}
      background="secondary"
      radius="md"
    >
      <Box flex="auto" gap="lg" minWidth="0">
        <Box direction="row" justifyContent="space-between" alignItems="flex-start" gap="md">
          <Box direction="column" gap="sm" flex="auto" minWidth="0">
            <Box fontSize="md" fontWeight="medium">
              {GeneratorEquipmentTypeLabels[item.type]}
            </Box>
            {item.manufacturer && item.model ? (
              <>
                <Box direction="row" gap="md" alignItems="center">
                  <Box fontSize="sm">
                    {item.manufacturer} - {item.model}
                    {powerRating ? ` ${powerRating}` : ''}
                  </Box>
                  <Button
                    as="button"
                    variant="tertiary"
                    size="sm"
                    isDisabled={isDisabled}
                    onClick={handleChangeProduct}
                  >
                    Change
                  </Button>
                </Box>
                <Box fontSize="sm">{item.quantity ? `Quantity: ${item.quantity}` : ''}</Box>
              </>
            ) : null}
            {showSearchSelector ? (
              <AsyncSelectInput
                id={`systems.${systemIndex}.${dataKey}.items.selectedItem`}
                name={`systems.${systemIndex}.${dataKey}.items.selectedItem`}
                label={`Search ${label}`}
                loadOnOpen
                loadOptions={async (input: string) => {
                  const options = await searchProducts(input, item.type);
                  return options ?? [];
                }}
                isDisabled={isDisabled}
                onChange={handleSelectProduct}
                onBlur={() => undefined}
                isClearable
                value={''}
                placeholder="Search by Model or Name..."
                noOptionsMessage={({ inputValue }: { inputValue: string | React.JSX.Element }) =>
                  inputValue ? 'Not found' : 'Type to search...'
                }
              />
            ) : null}
          </Box>
        </Box>

        <Box direction={{ base: 'column', desktop: 'row' }} gap={{ base: 'lg', desktop: 'xl' }}>
          <Box flex="auto" minWidth="0">
            <Box fontSize="sm" fontWeight="medium" margin="0 0 sm 0">
              Fuel Type
            </Box>
            <Controller
              control={control}
              name={fuelTypeName}
              render={({ field }) => (
                <RadioGroup
                  name={fuelTypeName}
                  value={field.value ?? ''}
                  options={fuelTypeOptions}
                  isDisabled={isDisabled}
                  onChange={(e) => field.onChange(e.target.value)}
                  onBlur={field.onBlur}
                />
              )}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export const EquipmentAndMaterialsSection = ({
  systemIndex,
  isPreliminary = false,
  helpLink,
  isPackageEditable = true,
}: EquipmentAndMaterialsSectionProps) => {
  const permissions = usePermissions();
  const userOnlyHasReaderPermission = permissions?.every((permission) =>
    permission.includes(UserPermissions.reader),
  );
  const isDisabled = userOnlyHasReaderPermission || !isPackageEditable;
  const { control, setValue } = useFormContext<any>();
  const { fields } = useFieldArray({
    control,
    name: `systems.${systemIndex}.${dataKey}.items`,
  });

  const [triggerSearch] = useSearchProductsMutation();
  const handleSearchText = useCallback(
    async (searchText: string) => {
      const { data, error } = await triggerSearch({
        ...(searchText ? { query: searchText } : {}),
        limit: 20,
        page: 1,
        filters: [
          {
            filter: 'classificationPath',
            operation: { equals: 'generators' },
          },
          {
            filter: 'availableOnDate',
            operation: { equals: new Date().toISOString() },
          },
        ],
      });
      if (error) {
        toast.error('Error searching products. Please try again.');
        return undefined;
      }
      const productData = data?.data?.map((product) => {
        // Catalog attribute values can be numbers; coerce to string to match the schema/form types.
        const lpPowerRating = String(
          product.attributes.find((attr) => attr.key === 'power-rating-lp-kw')?.value ?? '',
        );
        const gasPowerRating = String(
          product.attributes.find((attr) => attr.key === 'power-rating-gas-kw')?.value ?? '',
        );
        const model = product.attributes.find((attr) => attr.key === 'model-number')?.value ?? '';
        const powerRating = formatGeneratorPowerRating(lpPowerRating, gasPowerRating);
        return {
          label: `${product.manufacturer} - ${model}${powerRating ? ` ${powerRating}` : ''}`,
          value: product.id,
          manufacturer: product.manufacturer,
          model,
          lpPowerRating,
          gasPowerRating,
          productType: GeneratorEquipmentType.generator,
          name: product.name,
          isSerialNumberRequired: true,
        };
      });
      return productData?.length && (data?.paging?.totalPages || 0) > 1
        ? [
            {
              label: `${data?.paging.totalRecords} results found. Continue typing to narrow down results.`,
              value: undefined,
            },
            ...productData,
          ]
        : productData;
    },
    [triggerSearch],
  );

  const debouncedSearch = useMemo(
    () => debounceAsync((value: string) => handleSearchText(value), 400, 2, true),
    [handleSearchText],
  );

  // Ensure each system has a generator equipment item so the product catalog selector renders
  const equipmentItemsLength = fields.length;
  useEffect(() => {
    if (equipmentItemsLength === 0) {
      setValue(
        `systems.${systemIndex}.${dataKey}.items`,
        [{ type: GeneratorEquipmentType.generator, quantity: 1 }],
        { shouldDirty: true },
      );
    }
  }, [systemIndex, equipmentItemsLength, setValue]);

  const {
    isOpen: isQuantityModalOpen,
    handleOpen: openQuantityModal,
    handleClose: closeQuantityModal,
  } = useOpenClose();

  const handleQuantityChange = useCallback(
    (_index: number, newValue: number) => {
      if (newValue > 1) {
        openQuantityModal();
      }
    },
    [openQuantityModal],
  );

  if (isPreliminary) {
    return (
      <>
        <Modal
          isOpen={isQuantityModalOpen}
          onDismiss={closeQuantityModal}
          title="Additional Generator Required"
          size="sm"
        >
          <Box padding="lg" gap="lg">
            <Box>
              Each generator system must be entered separately. To add another generator, please use the &quot;Add
              System&quot; button below to create an additional system.
            </Box>
            <Box direction="row" justifyContent="flex-end">
              <Button variant="primary" onClick={closeQuantityModal}>
                Got it
              </Button>
            </Box>
          </Box>
        </Modal>
        <FormSection
          title={label}
          description={
            <Box gap="sm" fontSize="xs">
              <Box>Configure the generator specifications for this system.</Box>
              {helpLink && (
                <Box display="block">
                  For more information, check the{' '}
                  <Link to={helpLink} target="_blank" rel="noopener noreferrer">
                    Knowledge Base
                  </Link>
                </Box>
              )}
            </Box>
          }
          baseStyle={{
            borderWidth: '0',
            'data-intercom-target': intercomTarget('system-design', label),
          }}
        >
          <Box gap="xl">
            {fields.map((f, index) => (
              <PreliminaryEquipmentRow
                key={f.id}
                systemIndex={systemIndex}
                index={index}
                isDisabled={isDisabled}
                onQuantityChange={handleQuantityChange}
              />
            ))}
          </Box>
        </FormSection>
      </>
    );
  }

  // Non-preliminary (final) design UI
  return (
    <>
      <Modal
        isOpen={isQuantityModalOpen}
        onDismiss={closeQuantityModal}
        title="Additional Generator Required"
        size="sm"
      >
        <Box padding="lg" gap="lg">
          <Box>
            Each generator system must be entered separately. To add another generator, please use the &quot;Add
            System&quot; button below to create an additional system.
          </Box>
          <Box direction="row" justifyContent="flex-end">
            <Button variant="primary" onClick={closeQuantityModal}>
              Got it
            </Button>
          </Box>
        </Box>
      </Modal>
      <FormSection
        title={label}
        description={
          <Box gap="sm" fontSize="xs">
            <Box>Update applicable {label} for the proposal.</Box>
            {helpLink && (
              <Box display="block">
                For more information about System Categories, check the{' '}
                <Link to={helpLink} target="_blank" rel="noopener noreferrer">
                  Knowledge Base
                </Link>
              </Box>
            )}
          </Box>
        }
        baseStyle={{
          borderWidth: '0',
          'data-intercom-target': intercomTarget('system-design', label),
        }}
      >
        <Box gap="lg">
          {fields.map((f, index) => (
            <FinalEquipmentRow
              key={f.id}
              systemIndex={systemIndex}
              index={index}
              isDisabled={isDisabled}
              searchProducts={
                debouncedSearch as (input: string, productType: GeneratorEquipmentType) => Promise<any>
              }
            />
          ))}
        </Box>
      </FormSection>
    </>
  );
};
